import styles from './index.module.scss';

import React from 'react';
import { Container, Row, Col } from 'reactstrap';

import { ScrollOverPack, TweenOne, FadeIn, FadeUp, FadeLeft } from 'src/components/Animations';
import ButtonLink from 'src/components/ButtonLink';
import HelpIcon from 'src/components/HelpIcon';
import Link from 'gatsby-link';
import RetinaImage from 'src/components/RetinaImage';
import CarriersCarouselSection from 'src/components/CarriersCarouselSection';
import FreeTrialSection from 'src/components/FreeTrialSection';
import SavingsSection from 'src/components/SavingsSection';
import TestimonialsSection from 'src/components/TestimonialsSection';

import ImgIphoneAndLaptopDashboard from 'src/images/img-iphone-and-laptop-dashboard.png';
import ImgIphoneAndLaptopDashboard2x from 'src/images/img-iphone-and-laptop-dashboard@2x.png';

import IconAPI from 'src/images/icon-api.png';
import IconAPI2x from 'src/images/icon-api@2x.png';
import IconAPI3x from 'src/images/icon-api@3x.png';
import IconAuditing from 'src/images/icon-auditing.png';
import IconAuditing2x from 'src/images/icon-auditing@2x.png';
import IconAuditing3x from 'src/images/icon-auditing@3x.png';
import IconCarrierAgnostic from 'src/images/icon-carrier-agnostic.png';
import IconCarrierAgnostic2x from 'src/images/icon-carrier-agnostic@2x.png';
import IconCarrierAgnostic3x from 'src/images/icon-carrier-agnostic@3x.png';
import IconDashboard from 'src/images/icon-dashboard.png';
import IconDashboard2x from 'src/images/icon-dashboard@2x.png';
import IconDashboard3x from 'src/images/icon-dashboard@3x.png';
import IconOptimization from 'src/images/icon-optimization.png';
import IconOptimization2x from 'src/images/icon-optimization@2x.png';
import IconOptimization3x from 'src/images/icon-optimization@3x.png';
import IconReporting from 'src/images/icon-reporting.png';
import IconReporting2x from 'src/images/icon-reporting@2x.png';
import IconReporting3x from 'src/images/icon-reporting@3x.png';
import IconTeams from 'src/images/icon-teams.png';
import IconTeams2x from 'src/images/icon-teams@2x.png';
import IconTeams3x from 'src/images/icon-teams@3x.png';
import IconPayments from 'src/images/icon-payments.png';
import IconPayments2x from 'src/images/icon-payments@2x.png';
import IconPayments3x from 'src/images/icon-payments@3x.png';

const Hero = (props) => (
  <div styleName='hero'>
      <Container>
          <div styleName='text-container'>
              <div className='h1'>Shipping optimization</div>
              <div className='h1'>for the 21st century</div>
              <div className='h1 text-blue'>Welcome aboard.</div>
              <ButtonLink styleName='button' to='/contact'>
                  Try free for 7 days
              </ButtonLink>
          </div>
      </Container>
      <ScrollOverPack styleName='splash-image-container'>
          <TweenOne key='0'
                    component={RetinaImage}
                    srcSet={[ImgIphoneAndLaptopDashboard, ImgIphoneAndLaptopDashboard2x]}
                    styleName='splash-image'
                    {...FadeLeft} />
      </ScrollOverPack>
      <div styleName='bg-square-1' />
      <div styleName='bg-square-2' />
  </div>
);

const Features = (props) => (
  <ScrollOverPack component={Container} styleName='features' {...props}>
      <TweenOne key='headers' {...FadeIn}>
          <div className='h2'>
              Revolutionize how you ship
          </div>
          <div className='h2 text-blue'>
              Introducing Galleon.
          </div>
      </TweenOne>
      <TweenOne key='row-1' component={Row} {...FadeUp}>
          <Col md='4' xs='12' styleName='feature-cell'>
              <Link to='/features/dashboard'>
                  <div styleName='icon-container'>
                      <RetinaImage srcSet={[IconDashboard, IconDashboard2x, IconDashboard3x]} />
                  </div>
                  <div styleName='header'>Dashboard</div>
              </Link>
              <div>
                  See patterns and catch problems early with beautiful
                  dashboards. Get a big-picture view, or drill down with advanced
                  filtering. For the first time ever, “shipping” and “sexy” belong
                  in the same sentence.
              </div>
          </Col>
          <Col md='4' xs='12' styleName='feature-cell'>
              <Link to='/features/reporting'>
                  <div styleName='icon-container'>
                      <RetinaImage srcSet={[IconReporting, IconReporting2x, IconReporting3x]} />
                  </div>
                  <div styleName='header'>Reporting</div>
              </Link>
              <div>
                  Pull the data you need from dozens of templates, or customize
                  new ones. Schedule alerts and recurring reports to land in your
                  inbox whenever you want them. Data monkeys, meet your new
                  jungle gym.
              </div>
          </Col>
          <Col md='4' xs='12' styleName='feature-cell'>
              <Link to='/features/auditing'>
                  <div styleName='icon-container'>
                      <RetinaImage srcSet={[IconAuditing, IconAuditing2x, IconAuditing3x]} />
                  </div>
                  <div styleName='header'>Auditing</div>
              </Link>
              <div>
                  Reclaim up to 10% of your shipping spend with invoice and
                  contract auditing. Our AI-powered recovery engine finds errors
                  in your bills and request refunds, automatically.
                  Disclaimer: may cause euphoria in CFOs.
              </div>
          </Col>
      </TweenOne>
      <TweenOne key='row-2' component={Row} {...FadeUp}>
          <Col md='4' xs='12' styleName='feature-cell'>
              <Link to='/features/optimization'>
                  <div styleName='icon-container'>
                      <RetinaImage srcSet={[IconOptimization, IconOptimization2x, IconOptimization3x]} />
                  </div>
                  <div styleName='header'>Optimization</div>
              </Link>
              <div>
                  Discover how your costs compare to thousands of other shippers.
                  Save up to 40% as our experts fine-tune your carrier contracts
                  and routing logic. Not recommended for people who hate money.
              </div>
          </Col>
          <Col md='4' xs='12' styleName='feature-cell'>
              <Link to='/features/carrier-agnostic'>
                  <div styleName='icon-container'>
                      <RetinaImage srcSet={[IconCarrierAgnostic, IconCarrierAgnostic2x, IconCarrierAgnostic3x]} />
                  </div>
                  <div styleName='header'>Carrier agnostic</div>
              </Link>
              <div>
                  View all of your shipping data from UPS, FedEx, USPS, DHL and
                  other carriers in one centralized location. Never spend another
                  minute mashing spreadsheets together in Excel. Unless you’re
                  into that.
              </div>
          </Col>
          <Col md='4' xs='12' styleName='feature-cell'>
              <Link to='/features/teams'>
                  <div styleName='icon-container'>
                      <RetinaImage srcSet={[IconTeams, IconTeams2x, IconTeams3x]} />
                  </div>
                  <div styleName='header'>Teams</div>
              </Link>
              <div>
                  Give your whole team the gift of Galleon. Invite members,
                  revoke access and save your custom settings anytime. Because,
                  hey, everyone needs a little personal space.
              </div>
          </Col>
      </TweenOne>
      <TweenOne key='row-3' component={Row} {...FadeUp}>
          <Col md='4' xs='12' styleName='feature-cell'>
              <Link to='/features/payments'>
                  <div styleName='icon-container'>
                      <RetinaImage srcSet={[IconPayments, IconPayments2x, IconPayments3x]} />
                  </div>
                  <div styleName='header'>Payments</div>
              </Link>
              <div>
                  Improve cash flow, radically simplify bookkeeping, and save
                  even more money when you outsource your shipping A/P to
                  Galleon. Save an accountant’s life today.
              </div>
          </Col>
          <Col md='4' xs='12' styleName='feature-cell'>
              <Link to='/features/api'>
                  <div styleName='icon-container'>
                      <RetinaImage srcSet={[IconAPI, IconAPI2x, IconAPI3x]} />
                  </div>
                  <div styleName='header'>API</div>
              </Link>
              <div>
                  Pipe reliable post-shipment data directly into your own data
                  warehouse. Join shipping data with other customer information
                  in Looker, Tableau or your preferred BI tool. You’re welcome,
                  nerd.
              </div>
          </Col>
          <Col md='4' xs='12' styleName='feature-cell learn-more'>
              <div styleName='icon-container'>&nbsp;</div>
              <div styleName='header'>Ready to learn more?</div>
              <ButtonLink to='/contact'>
                  Contact us for a demo
              </ButtonLink>
          </Col>
      </TweenOne>
  </ScrollOverPack>
);

const IndexPage = () => (
  <div>
      <Hero />
      <Features />
      <SavingsSection className='anchor-target' id='savings' />
      <TestimonialsSection className='anchor-target' id='testimonials' />
      <FreeTrialSection />
      <CarriersCarouselSection />
  </div>
);

export default IndexPage;



// WEBPACK FOOTER //
// ./src/pages/index.js